import * as React from 'react';
import {RefObject, useRef} from 'react';
import {FormattedMessage} from 'react-intl';

import { useDeviceStore, selectIsMobile } from 'features/ui/store/useDeviceStore';
import {useModal} from 'features/modal/hooks/useModal';
import {getFormattedMessage} from 'features/services/utils/language-helper';
import {ImageSizes} from 'components/organisms/Shell/image-size-enum';
import {getImageUrlFromCDN} from 'components/atoms/Media/AsyncImage/helper';
import {getObjectImageByType, getObjectImagesByType} from 'features/services/utils/image-helpers';

import './style.scss';

import {P} from 'components/atoms/Text/P';
import {Icon} from 'components/atoms/Media/Icon';
import {Button, ButtonColor, ButtonSize, ButtonType} from 'components/atoms/LinksAndButtons/Button';
import {MainGrid} from 'components/atoms/Layout/MainGrid';
import {Container} from 'components/atoms/Layout/Container';
import {AsyncImage} from 'components/atoms/Media/AsyncImage';
import {ModalGallery} from 'components/modals/General/ModalGallery';
import {Tab} from 'components/organisms/ContentBlocks/TabbedSection/Tab';
import {TabbedSection} from 'components/organisms/ContentBlocks/TabbedSection';
import {TabBar} from 'components/organisms/ContentBlocks/TabbedSection/TabBar';
import {TabContent} from 'components/organisms/ContentBlocks/TabbedSection/TabContent';
import {useResidenceExplorerStore, selectBalconyView, selectObject3DView} from "features/residenceExplorer/store/useResidenceExplorerStore";
import {ResidenceExplorerResponse} from 'features/residenceExplorer/types';
import {EpiImage, EpiImageWithType} from "features/search/types";
import {ObjectImageType} from 'features/template/viewModels';

import {ApartmentViewerSunStudy} from "features/apartmentViewer/components/ApartmentViewerSunStudy";
import {Headline} from '@atoms/Text/Headline';
import {HeadlineLevel} from 'features/ui/enums';

interface Props {
    images: EpiImageWithType[];
    residenceExplorerData?: ResidenceExplorerResponse | null;
    stage?: string;
    name?: string;
    hideFloorPlan?: boolean;
    innerRef?: RefObject<HTMLDivElement> | null;
    has360?: boolean;
}

export const FloorplanTabber: React.FunctionComponent<Props> = ({images, hideFloorPlan, innerRef, has360, stage, name}) => {
    const scrollRef = useRef(null);
    const { open } = useModal();
    const balconyView = useResidenceExplorerStore(selectBalconyView);
    const object3DView = useResidenceExplorerStore(selectObject3DView);
    const isMobile = useDeviceStore(selectIsMobile);

    const openFloorplanGallery = (index: number) => () => {
        const fetchedImages: EpiImage[] = [];
        const floorplan = getObjectImagesByType(images, [ObjectImageType.FloorPlan, ObjectImageType.AltFloorPlan]);
        const situationPlan = getObjectImageByType(images, ObjectImageType.Situation);

        if (floorplan !== null) {
            floorplan.map((image) => {
                fetchedImages.push(image);
            });
        }

        if (situationPlan != null) fetchedImages.push(situationPlan);

        open(
            <ModalGallery images={fetchedImages} startIndex={index}/>,
            {fullscreen: true, ariaLabel: getFormattedMessage('jm.components.modalGallery.ariaLabel'), triggerElementId: 'floorplanButton' }
        );
    };

    const openBalconyGallery = (index: number) => () => {
        const fetchedImages: EpiImage[] = [];

        balconyView.map(balcony => fetchedImages.push({ url: balcony }));

        open(
            <ModalGallery images={fetchedImages} startIndex={index}/>,
            {fullscreen: true, ariaLabel: getFormattedMessage('jm.components.modalGallery.ariaLabel'), triggerElementId: 'balconyButton' }
        );
    };

    const scrollToReferenceCallback = (reference: React.RefObject<HTMLDivElement | null> | null | undefined) => () => {
        if (!reference || reference.current === null) return;

        window.scroll({
            top: (reference.current.getBoundingClientRect().top + window.scrollY) - (isMobile ? 78 : 121),
            behavior: 'smooth'
        });
    };

    const ShowApartmentSunstudy = () => {

        if(!stage || !name) return;

        open(
            <ApartmentViewerSunStudy stageId={stage} objectName={name} />,
            {fullscreen: true, ariaLabel: getFormattedMessage('jm.components.apartmentViewerSunStudy.ariaLabel'), triggerElementId: 'sunstudy-360-button' }
        );
    }

    const floorplan = getObjectImagesByType(images, [ObjectImageType.FloorPlan, ObjectImageType.AltFloorPlan]);
    const situationPlan = getObjectImageByType(images, ObjectImageType.Situation);
    const interiorImage = getObjectImageByType(images, ObjectImageType.Interior);
    const exteriorImage = getObjectImageByType(images, ObjectImageType.Exterior);
    let floorplanLength = 0;
    let defaultTab = 0;

    if (floorplan && floorplan.length > 0) floorplanLength = floorplan.length;
    if (situationPlan) floorplanLength++;

    if (floorplan === null || hideFloorPlan) {
        if (balconyView.length > 0) {
            defaultTab = 2;
        } else {
            if (object3DView.length > 0) {
                defaultTab = 1;
            }
        }
    }

    return (
        <div ref={typeof innerRef !== 'undefined' ? innerRef : scrollRef}>
            {(floorplan !== null || situationPlan !== null || balconyView.length > 0 || object3DView.length > 0) &&
                <Container>
                    <TabbedSection
                        defaultTab={defaultTab}
                        switchTabCallback={scrollToReferenceCallback(innerRef !== null ? innerRef : scrollRef)}
                    >
                        <div>
                            <TabBar>
                                {((floorplan != null || situationPlan != null) && !hideFloorPlan) &&
                                    <Tab
                                        title={getFormattedMessage('jm.components.ObjectPage.floorplanHeadline')}
                                        index={0}
                                        icon={1}
                                    />
                                }
                                {has360 &&
                                    <Tab
                                        title={"Solstudie"}
                                        index={1}
                                        icon={2}
                                    />
                                }
                                {object3DView.length > 0 &&
                                    <Tab
                                        title={getFormattedMessage('jm.components.ObjectPage.object3dViewHeadline')}
                                        index={2}
                                        icon={2}
                                    />
                                }
                                {balconyView.length > 0 &&
                                    <Tab
                                        title={getFormattedMessage('jm.components.ObjectPage.balconyViewHeadline')}
                                        index={3}
                                        icon={3}
                                    />
                                }
                            </TabBar>

                            {((floorplan != null || situationPlan != null) && !hideFloorPlan) &&
                                <TabContent index={0}>
                                    <MainGrid fullwidth>
                                        <div className="tabbed-section__text">
                                            <Headline level={HeadlineLevel.H2}>
                                                <FormattedMessage id='jm.components.ObjectPage.floorplanHeadline'/>
                                            </Headline>
                                            <P>
                                                <FormattedMessage id='jm.components.ObjectPage.floorplanText'/>
                                            </P>
                                            {(floorplanLength > 1) &&
                                                <Button
                                                    onClick={openFloorplanGallery(0)}
                                                    id='floorplanButton'
                                                    buttonColor={ButtonColor.DEFAULT}
                                                    buttonSize={ButtonSize.DEFAULT}
                                                    buttonType={ButtonType.GHOST}
                                                >
                                                    <FormattedMessage
                                                        id='jm.components.ObjectPage.floorplanButton'/> ({floorplanLength})
                                                </Button>
                                            }
                                        </div>
                                        <div className="tabbed-section__image tabbed-section__floorplan-image">
                                            <Button
                                                className="tabbed-section__fullscreen-icon"
                                                onClick={openFloorplanGallery(0)}
                                                icon={<Icon name="fullscreen" />}
                                                iconBtn
                                                id={floorplanLength > 1 ? '' : 'floorplanButton'}
                                                buttonColor={ButtonColor.UNSET}
                                                buttonSize={ButtonSize.DEFAULT}
                                                buttonType={ButtonType.NO_STYLE}
                                            >
                                                <FormattedMessage id='jm.components.ObjectPage.floorplanButton'/>
                                            </Button>

                                            {floorplan !== null &&
                                                <img
                                                    src={getImageUrlFromCDN(floorplan[0] as EpiImage, ImageSizes.SIZE_900, false)}
                                                    onClick={openFloorplanGallery(0)}
                                                    alt="Floorplan"
                                                />
                                            }
                                            {floorplan === null && situationPlan !== null &&
                                                <img
                                                    src={getImageUrlFromCDN(situationPlan as EpiImage, ImageSizes.SIZE_900, false)}
                                                    onClick={openFloorplanGallery(floorplanLength - 1)}
                                                    alt="situationPlan"
                                                />
                                            }
                                        </div>
                                    </MainGrid>
                                </TabContent>
                            }

                            {has360 &&
                                <TabContent index={1}>
                                    <MainGrid fullwidth>
                                        <div className="tabbed-section__text">
                                            <Headline level={HeadlineLevel.H2}>
                                                <FormattedMessage id="jm.components.ObjectPage.sunStudyTab.title" />
                                            </Headline>

                                            <P>
                                                <FormattedMessage id="jm.components.ObjectPage.sunStudyTab.content" />
                                            </P>

                                            <Button
                                                onClick={ShowApartmentSunstudy}
                                                buttonColor={ButtonColor.DEFAULT}
                                                buttonSize={ButtonSize.DEFAULT}
                                                buttonType={ButtonType.DEFAULT}
                                                id="sunstudy-360-button"
                                            >
                                                <FormattedMessage id='jm.components.ResidenceExplorer.viewSunStudy'/>
                                            </Button>
                                        </div>
                                        <div className="tabbed-section__image">
                                            {images && interiorImage &&
                                                <AsyncImage
                                                    image={interiorImage}
                                                    fullWidth
                                                    quality={ImageSizes.SIZE_900}
                                                />
                                            }
                                        </div>
                                    </MainGrid>
                                </TabContent>
                            }

                            {object3DView.length > 0 &&
                                <TabContent index={2}>
                                    <MainGrid fullwidth>
                                        <div className="tabbed-section__text">
                                            <Headline level={HeadlineLevel.H2}>
                                                <FormattedMessage id='jm.components.ObjectPage.object3dViewHeadline'/>
                                            </Headline>
                                            <P>
                                                <FormattedMessage id='jm.components.ObjectPage.object3dViewText'/>
                                            </P>

                                            <Button
                                                onClick={() => window.open(object3DView[0], '_blank', 'noopener,noreferrer')}
                                                buttonColor={ButtonColor.DEFAULT}
                                                buttonSize={ButtonSize.DEFAULT}
                                                buttonType={ButtonType.DEFAULT}
                                            >
                                                <FormattedMessage id='jm.components.ObjectPage.object3dViewButton'/>
                                            </Button>
                                        </div>
                                        <div className="tabbed-section__image">
                                            {images && interiorImage &&
                                                <AsyncImage
                                                    image={interiorImage}
                                                    fullWidth
                                                    quality={ImageSizes.SIZE_900}
                                                />
                                            }
                                        </div>
                                    </MainGrid>
                                </TabContent>
                            }

                            {balconyView.length > 0 &&
                                <TabContent index={3}>
                                    <MainGrid fullwidth>
                                        <div className="tabbed-section__text">
                                            <Headline level={HeadlineLevel.H2}>
                                                <FormattedMessage id='jm.components.ObjectPage.balconyViewHeadline'/>
                                            </Headline>
                                            <P>
                                                <FormattedMessage id='jm.components.ObjectPage.balconyViewText'/>
                                            </P>
                                            {balconyView[0].indexOf(".html") > -1 &&
                                                <Button
                                                    onClick={() => window.open(balconyView[0], '_blank', 'noopener,noreferrer')}
                                                    buttonColor={ButtonColor.DEFAULT}
                                                    buttonSize={ButtonSize.DEFAULT}
                                                    buttonType={ButtonType.DEFAULT}
                                                >
                                                    <FormattedMessage id='jm.components.ObjectPage.balconyButtonExternal'/>
                                                </Button>
                                            }
                                            {balconyView[0].indexOf(".html") === -1 && balconyView.length > 1 &&
                                                <Button
                                                    onClick={openBalconyGallery(0)}
                                                    id='balconyButton'
                                                    buttonColor={ButtonColor.DEFAULT}
                                                    buttonSize={ButtonSize.DEFAULT}
                                                    buttonType={ButtonType.GHOST}
                                                >
                                                    <FormattedMessage id='jm.components.ObjectPage.balconyButton'/> ({balconyView.length})
                                                </Button>
                                            }
                                        </div>

                                        {balconyView[0].indexOf(".html") === -1 &&
                                            <div className="tabbed-section__image tabbed-section__balcony-image">
                                                <Button
                                                    className="tabbed-section__fullscreen-icon"
                                                    onClick={openBalconyGallery(0)}
                                                    icon={<Icon name="fullscreen" />}
                                                    iconBtn
                                                    id={balconyView[0].indexOf(".html") === -1 && balconyView.length > 1 ? '' : 'balconyButton'}
                                                    buttonColor={ButtonColor.UNSET}
                                                    buttonSize={ButtonSize.DEFAULT}
                                                    buttonType={ButtonType.NO_STYLE}
                                                >
                                                    <FormattedMessage id='jm.components.ObjectPage.balconyButton'/>
                                                </Button>
                                                <AsyncImage
                                                    src={balconyView[0]}
                                                    quality={ImageSizes.SIZE_900}
                                                    fullWidth
                                                    onClick={openBalconyGallery(0)}
                                                    alt={getFormattedMessage('jm.components.ObjectPage.balconyViewHeadline')}
                                                />
                                            </div>
                                        }

                                        {balconyView[0].indexOf(".html") > -1 &&
                                            <div className="tabbed-section__image tabbed-section__balcony-image">
                                                {exteriorImage &&
                                                    <AsyncImage
                                                        image={exteriorImage}
                                                        quality={ImageSizes.SIZE_900}
                                                        fullWidth
                                                    />
                                                }
                                            </div>
                                        }
                                    </MainGrid>
                                </TabContent>
                            }
                        </div>
                    </TabbedSection>
                </Container>
            }
        </div>
    );
};
